import { RIYAL } from "@/lib/currency";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatNumber } from "@/lib/formatters";
import { sumUnitRents } from "@/lib/contractHelpers";
import {
  ALL_SERVICE_KEYS,
  SERVICE_SHORT_LABELS,
  type AnyServiceKey,
} from "./wizard-v2/types";

// Read-only row shape — covers the wizard's in-progress form rows AND
// junction rows after persistence. Each consumer maps its data to this
// minimal shape so the table stays storage-agnostic.
//
// `serviceFees[key]` is the displayed annual amount for that service.
// `payer` (when present) lets the table mark tenant-paid amounts so the
// user immediately sees what flows into rent invoices.
export interface UnitsBreakdownRow {
  key: string;
  unitLabel: string;
  rentAmount: number;
  serviceFees: Partial<Record<AnyServiceKey, { amount: number | null; payer?: "tenant" | "owner" } | number | null>>;
}

interface UnitsBreakdownTableProps {
  rows: UnitsBreakdownRow[];
}

// Pulls a number out of the union shape (legacy plain-number OR new
// `{ amount, payer }` object). Returns null when the entry isn't usable
// so the cell renders an em-dash instead of "0" / "NaN".
function readAmount(
  entry: { amount: number | null; payer?: "tenant" | "owner" } | number | null | undefined
): number | null {
  if (entry == null) return null;
  if (typeof entry === "number") return entry > 0 ? entry : null;
  if (typeof entry === "object" && entry.amount != null && entry.amount > 0) return entry.amount;
  return null;
}

function readPayer(
  entry: { amount: number | null; payer?: "tenant" | "owner" } | number | null | undefined
): "tenant" | "owner" | undefined {
  if (entry && typeof entry === "object" && "payer" in entry) return entry.payer;
  return undefined;
}

// Renders a per-unit summary: one row per unit with rent + 8 service
// columns (4 EJAR fees + 4 utilities) + a total row that sums the rents.
// Consumed by ContractUnitsCard (post-save) and ReviewStep (pre-submit).
export function UnitsBreakdownTable({ rows }: UnitsBreakdownTableProps) {
  const { t } = useLanguage();
  const totalRent = sumUnitRents(rows.map((r) => ({ rent_amount: r.rentAmount })));

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("الوحدة", "Unit")}</TableHead>
          <TableHead className="text-end">
            {t("الإيجار السنوي (قبل الضريبة)", "Annual Rent (excl. VAT)")}
          </TableHead>
          {ALL_SERVICE_KEYS.map((key) => (
            <TableHead key={key} className="text-end text-xs">
              {t(SERVICE_SHORT_LABELS[key].ar, SERVICE_SHORT_LABELS[key].en)}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.key}>
            <TableCell className="font-medium">{row.unitLabel}</TableCell>
            <TableCell className="text-end">
              {row.rentAmount > 0
                ? `${formatNumber(row.rentAmount)}\u00A0${RIYAL}`
                : "—"}
            </TableCell>
            {ALL_SERVICE_KEYS.map((key) => {
              const amount = readAmount(row.serviceFees[key]);
              const payer = readPayer(row.serviceFees[key]);
              return (
                <TableCell key={key} className="text-end text-xs">
                  {amount != null ? (
                    <span>
                      {formatNumber(amount)}
                      {payer === "tenant" && (
                        <span className="ms-1 text-[10px] text-muted-foreground">
                          {t("(م)", "(T)")}
                        </span>
                      )}
                      {payer === "owner" && (
                        <span className="ms-1 text-[10px] text-muted-foreground">
                          {t("(م.ك)", "(O)")}
                        </span>
                      )}
                    </span>
                  ) : (
                    "—"
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
        <TableRow className="font-semibold bg-muted/30">
          <TableCell>{t("الإجمالي", "Total")}</TableCell>
          <TableCell className="text-end">
            {`${formatNumber(totalRent)}\u00A0${RIYAL}`}
          </TableCell>
          <TableCell colSpan={ALL_SERVICE_KEYS.length} />
        </TableRow>
      </TableBody>
    </Table>
  );
}

export default UnitsBreakdownTable;
